import { type MutableRefObject, useEffect, useState } from 'react';

type UseActiveCardObserverProps = {
  cardRefs: MutableRefObject<(HTMLElement | null)[]>;
  childCount: number;
  options: {
    isEnabled: boolean;
    threshold?: number;
  };
};

export const useActiveCardObserver = ({
  cardRefs,
  childCount,
  options,
}: UseActiveCardObserverProps): number => {
  const { threshold = 0.5, isEnabled } = options;
  const [activeCardIndex, setActiveCardIndex] = useState(0);

  useEffect(() => {
    // If this hook is disabled (e.g., on mobile), do nothing.
    if (!isEnabled) {
      // You might want to reset the index when it becomes disabled
      setActiveCardIndex(0);
      return;
    }

    const cardElements = cardRefs.current.slice(0, childCount);

    const observerCallback = (entries: IntersectionObserverEntry[]) => {
      for (const entry of entries) {
        if (entry.isIntersecting) {
          const cardIndex = cardElements.indexOf(entry.target as HTMLDivElement);
          setActiveCardIndex(cardIndex);
        }
      }
    };

    const observer = new IntersectionObserver(observerCallback, { threshold });

    for (const cardEl of cardElements) {
      if (cardEl) {
        observer.observe(cardEl);
      }
    }

    return () => {
      observer.disconnect();
    };
  }, [cardRefs, childCount, threshold, isEnabled]);

  return activeCardIndex;
};
